import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from './hooks/useAuth';
import { useClub, ClubProvider } from './hooks/useClub';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import CheckInSidebar from './components/CheckInSidebar';
import T2MModule from './modules/t2m/T2MModule';
import ClubDashboardModule from './modules/dashboard/ClubDashboardModule';
import PB101Module from './modules/pb101/PB101Module';
import PB101Kanban from './modules/pb101/PB101Kanban';
import RetentionKanban from './modules/retention/RetentionKanban';
import T2MKanban from './modules/t2m/T2MKanban';
import LeadEngineKanban from './modules/lead/LeadEngineKanban';
import PB101RecordDetail from './modules/pb101/PB101RecordDetail';
import LeadModule from './modules/lead/LeadModule';
import KPIDashboard from './modules/dashboard/KPIDashboard';
import UploadPortal from './pages/UploadPortal';
import AdminModule from './modules/admin/AdminModule';
import MismatchModule from './modules/mismatch/MismatchModule';
import RevenueModule from './modules/revenue/RevenueModule';
import ActivationsModule from './modules/activations/ActivationsModule';
import EventsSponsorshipModule from './modules/events/EventsSponsorshipModule';
import InboundLeadsModule from './components/InboundLeadsModule';
import { LeadFollowUps } from './modules/lead/LeadContacts';
import MemberSearchModule from './modules/crm/MemberSearchModule';
import { LeadContactLog } from './modules/lead/LeadContacts';
import ChangelogModule from './modules/changelog/ChangelogModule';
import SalesDashboardModule from './modules/sales-dashboard/SalesDashboardModule';
import CommissionModule from './modules/commission/CommissionModule';
import AmbassadorLogPage from './pages/AmbassadorLogPage';
import AmbassadorModule from './modules/ambassador/AmbassadorModule';

const LOGO_URL = 'https://murkostorage.blob.core.windows.net/branding/coms-logo.png';

// PB101 kanban pilot rollout (BK, 9/18/26) — club codes here get FD/Sales
// access to the new board; every other club stays admin-only until trained.
// Empty until volunteer clubs are confirmed. Keep in sync with the same
// list in AzureApp/src/index.js (pb101-kanban route) — that's the real
// enforcement point (API-level), this only controls the route/nav in the UI.
const PB101_KANBAN_PILOT_CLUBS = ['MCK', 'GB', 'MF', 'WA', 'LEW'];

// Placeholder for CRM routes — built in future sprint
function CrmPlaceholder({ label }) {
  return (
    <div style={{ padding: 32, color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 13 }}>Coming soon — Sprint 18</div>
    </div>
  );
}

// BUG FIX (Sprint 19.4, second pass): the first version of this fix rendered
// PB101RecordDetail alongside/below LeadFollowUps, but PB101RecordDetail is a
// normal block-flow component (position: static), not an overlay/modal. That
// meant it rendered correctly, with real data, but ~4,975px down the page —
// past dozens of follow-up cards, completely out of view without scrolling.
// Confirmed live: the panel WAS mounting (network request + real DOM content
// verified), just invisible in practice. Fixed by swapping the whole view
// instead of appending, matching the existing selectedId/LeadProspectDetail
// full-page-replacement pattern already used in LeadModule.jsx.
function FollowUpsPage() {
  const [pb101ViewId, setPb101ViewId] = useState(null);
  if (pb101ViewId) {
    return (
      <PB101RecordDetail
        recordId={pb101ViewId}
        onBack={() => setPb101ViewId(null)}
        onLogContact={() => setPb101ViewId(null)}
      />
    );
  }
  return (
    <LeadFollowUps
      onSelectProspect={(id, source) => {
        if (source === 't2m') window.location.href = '/t2m?trialId=' + id;
        else if (source === 'pb101') setPb101ViewId(id);
      }}
      tab="all"
    />
  );
}

function AppRoutes() {
  const { role, hasModule } = useAuth();
  const { currentClub } = useClub();

  const isFd    = role === 'fd';
  const isMgr   = role === 'mgr' || role === 'manager';
  const isSales = role === 'sales';
  const isAdmin = role === 'admin';
  const isMloc  = role === 'mloc';
  const isClub  = isFd || isMgr;

  const canUpload      = isAdmin;
  const canSales       = isAdmin || isMloc || isSales;
  const canClub        = isAdmin || isMloc || isClub;
  // T2M and PB101 are dual-owned with sales (Josh closes T2M at day-10
  // escalation and owns PB101 follow-up for online/MS-sourced leads) — see
  // Sidebar.jsx for the matching split. Retention stays on canClub alone,
  // no sales overlap, per the ownership model.
  const canSharedClub  = canClub || isSales;
  const canEvents      = isAdmin || isMloc;
  const canSuppression = isAdmin || isMloc;

  const clubKey = currentClub?.id || 'default';

  const defaultRoute = isFd   ? '/pb101'
    : canSales                ? '/lead'
    : canClub                 ? '/t2m'
    : '/crm/search';

  return (
    <div className="app-layout">
      <Topbar />
      <div className="app-body">
        <Sidebar />
        <main className="app-main">
          <Routes>
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />

            {/* ── SALES ── */}
            <Route path="/lead/*"
              element={canSales && hasModule('lead_engine')
                ? <LeadModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/inbound/*"
              element={canSales
                ? <InboundLeadsModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/followups/*"
              element={canSales
                ? <FollowUpsPage />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Lead Engine Kanban, Sprint 21.3 (9/25/26) — same canSales gate
                as /lead itself (no club-side track — clubs don't have
                access to Lead Engine at all). Additive alongside the
                existing 4-tab /lead view, not a replacement yet. */}
            <Route path="/lead-pipeline/*"
              element={canSales && hasModule('lead_engine')
                ? <LeadEngineKanban key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Sales Dashboard, Sprint 21.3 (10/1/26) — same access
                population as Lead Engine Kanban (Josh's page); an
                additional page alongside his existing default landing
                route (/lead), not a replacement for it yet. */}
            <Route path="/sales-dashboard/*"
              element={canSales && hasModule('lead_engine')
                ? <SalesDashboardModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/commission/*"
              element={(isAdmin || isMloc || isSales)
                ? <CommissionModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />

            {/* ── CLUB ── */}
            <Route path="/club-dashboard/*"
              element={canClub && hasModule('club_dashboard')
                ? <ClubDashboardModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/t2m/*"
              element={canSharedClub && hasModule('t2m')
                ? <T2MModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* T2M Kanban, Sprint 21.3 (9/28/26) — same canSharedClub gate as
                /t2m itself. FD access on this board's routes was a real gap
                found and fixed backend-side (T2M_KANBAN_ROLES, AzureApp
                src/index.js); the old /t2m view's own routes stay unchanged. */}
            <Route path="/t2m-pipeline/*"
              element={canSharedClub && hasModule('t2m')
                ? <T2MKanban key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Old Retention (Daily List / Backlog) retired 10/6/26 — the Retention Pipeline replaced it. */}
            <Route path="/retention/*" element={<Navigate to="/retention-pipeline" replace />} />
            <Route path="/ambassador/*"
              element={canClub && hasModule('ambassador')
                ? <AmbassadorModule key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Renewal Reminders page retired 10/6/26 — its cards now live in the Retention Pipeline's Renewal Window column. */}
            <Route path="/renewal-reminders/*" element={<Navigate to="/retention-pipeline" replace />} />
            <Route path="/pb101/*"
              element={canSharedClub && hasModule('pb101')
                ? <PB101Module key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Pipeline redesign board, Sprint 21.3. Rollout (BK, 9/18/26;
                expanded 9/29/26): admin always sees it; FD/Manager get it
                only at volunteer pilot clubs (see PB101_KANBAN_PILOT_CLUBS
                below) while feedback comes in, before training the rest —
                now MCK + GB. Sales (Josh) is NOT pilot-club-gated at all:
                he works leads across every club, so he gets this for all 6
                regardless of the pilot list. Keep this array in sync with
                the same list in AzureApp/src/index.js (pb101-kanban route)
                — that's the real enforcement point, this is just what
                decides whether the nav link/route render. */}
            <Route path="/pb101-pipeline/*"
              element={(isAdmin || isSales || (canClub && PB101_KANBAN_PILOT_CLUBS.includes(currentClub?.id)))
                ? <PB101Kanban key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            {/* Retention Kanban, Sprint 21.2 (9/24/26). No pilot-club
                restriction, unlike PB101 Pipeline above — BK asked for this
                made available to him and to Josh (sales) from the start.
                Real enforcement is still API-side (retention-kanban route,
                AzureApp/src/index.js, via clubFilter(user)); this only
                controls the route/nav in the UI. */}
            <Route path="/retention-pipeline/*"
              element={canSharedClub
                ? <RetentionKanban key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/mismatch/*"
              element={<MismatchModule key={clubKey} />}
            />

            {/* ── CRM ── */}
            <Route path="/crm/search"
              element={<MemberSearchModule />}
            />
            <Route path="/crm/log"
              element={<LeadContactLog />}
            />
            <Route path="/crm/suppression"
              element={canSuppression
                ? <CrmPlaceholder label="Suppression List" />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/changelog"
              element={<ChangelogModule />}
            />

            {/* ── EVENTS & SPONSORSHIP ── */}
            <Route path="/es-sales"
              element={canEvents
                ? <EventsSponsorshipModule />
                : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/activations/*"
              element={canEvents
                ? <ActivationsModule />
                : <Navigate to={defaultRoute} replace />}
            />

            {/* ── ADMIN ── */}
            <Route path="/revenue/*"
              element={canUpload ? <RevenueModule key={clubKey} /> : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/upload/*"
              element={canUpload ? <UploadPortal /> : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/admin/*"
              element={<AdminModule />}
            />

            {/* Legacy dashboard route */}
            <Route path="/dashboard/*"
              element={hasModule('dashboard')
                ? <KPIDashboard key={clubKey} />
                : <Navigate to={defaultRoute} replace />}
            />

            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </main>
      </div>
      <CheckInSidebar />
    </div>
  );
}

function ProtectedApp() {
  return (
    <ClubProvider>
      <AppRoutes />
    </ClubProvider>
  );
}

function LoginScreen() {
  const { loginWithRedirect } = useAuth0();
  return (
    <div className="login-screen">
      <div className="login-card">
        <img
          src={LOGO_URL}
          alt="COMS"
          style={{ height: 100, width: 'auto', marginBottom: 16 }}
        />
        <div style={{ marginBottom: 4, fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '0.04em' }}>
          Club Operations Manager
        </div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 28, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Powered by Murko Capital
        </div>
        <button className="login-btn" onClick={() => loginWithRedirect()}>
          Sign In
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth0();

  // Ambassador's personal log link — no COMS login exists for an Ambassador
  // (they're a member, not staff), so this must be checked before the Auth0
  // gate below, not inside ProtectedApp's route table. See
  // docs/COMS_Ambassador_Program_Build_Spec.md.
  if (window.location.pathname.startsWith('/ambassador-log/')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/ambassador-log/:code" element={<AmbassadorLogPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (isLoading) return <div className="loading-screen">Loading...</div>;
  return (
    <BrowserRouter>
      {isAuthenticated ? <ProtectedApp /> : <LoginScreen />}
    </BrowserRouter>
  );
}
